/**
 * Proof management utilities for converting between cashu-ts and Evolu formats.
 *
 * The cashu-ts library uses its own Proof type with fields { amount, secret, C, id }.
 * The Evolu cashuToken table uses { amount, secret, c, keysetId, state, ... }.
 * These utilities handle the bidirectional conversion and grouping.
 *
 * SECURITY: These functions handle bearer token data. Never log proof secrets
 * or C values. Only pass them through for storage/retrieval.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module cashu/utils/proof-management
 */

/**
 * Minimal cashu-ts Proof interface.
 * Matches the fields returned by cashu-ts Wallet operations.
 */
export interface CashuProof {
  /** Amount in satoshis. */
  readonly amount: number;
  /** Secret used for spending (bearer credential). SENSITIVE. */
  readonly secret: string;
  /** Blinded signature from the mint. SENSITIVE. */
  readonly C: string;
  /** Keyset ID this proof belongs to. */
  readonly id: string;
}

/**
 * Evolu cashuToken row format for storage.
 * Matches the cashuToken table schema in DATA_SCHEMA.md.
 */
export interface EvoluTokenRow {
  /** Reference to the CashuMint row ID. */
  readonly mintId: string;
  /** Amount in satoshis. */
  readonly amount: number;
  /** Secret used for spending. SENSITIVE. */
  readonly secret: string;
  /** Blinded signature (lowercase 'c' in Evolu). SENSITIVE. */
  readonly c: string;
  /** Keyset ID this proof belongs to. */
  readonly keysetId: string;
  /** Token state: 'active', 'pending', or 'spent'. */
  readonly state: string;
  /** P2PK lock target pubkey, or null. */
  readonly lockedTo: string | null;
  /** ISO timestamp when spent, or null. */
  readonly spentAt: string | null;
  /** Transaction ID that spent this token, or null. */
  readonly spentInTxId: string | null;
  /** Derivation method: 'random', 'deterministic', or null (legacy). */
  readonly derivation: string | null;
  /** Counter value for deterministic tokens, or null for random. */
  readonly derivationCounter: number | null;
}

/**
 * Derivation info for proof-to-Evolu conversion.
 * Indicates whether proofs use random or deterministic secrets.
 */
export type DerivationInfo =
  | { readonly type: 'random' }
  | { readonly type: 'deterministic'; readonly counter: number };

/**
 * Convert cashu-ts Proof objects to Evolu cashuToken insert format.
 *
 * Maps cashu-ts field names to Evolu column names:
 * - C -> c (lowercase)
 * - id -> keysetId
 *
 * All converted tokens start in 'active' state with null optional fields.
 *
 * @param proofs - Array of cashu-ts Proof objects
 * @param mintId - The Evolu CashuMint row ID these proofs belong to.
 *   IMPORTANT: This must be the current session's row ID, not a cached/stored ID
 *   from a previous session. After restore, row IDs change. Callers should use
 *   `useMintLookup().findByUrl(url)?.id` to get the correct current-session ID.
 * @param derivationInfo - Optional derivation info (random or deterministic with counter)
 * @returns Array of objects ready for Evolu insert
 */
export function proofsToEvolu(
  proofs: readonly CashuProof[],
  mintId: string,
  derivationInfo?: DerivationInfo,
): EvoluTokenRow[] {
  return proofs.map((proof, index) => ({
    mintId,
    amount: proof.amount,
    secret: proof.secret,
    c: proof.C,
    keysetId: proof.id,
    state: 'active',
    lockedTo: null,
    spentAt: null,
    spentInTxId: null,
    derivation: derivationInfo?.type ?? 'random',
    derivationCounter:
      derivationInfo?.type === 'deterministic' ? derivationInfo.counter + index : null,
  }));
}

/**
 * Convert Evolu cashuToken rows back to cashu-ts Proof format.
 *
 * Maps Evolu column names to cashu-ts field names:
 * - c -> C (uppercase)
 * - keysetId -> id
 *
 * Only includes the 4 fields cashu-ts expects for proof operations.
 *
 * @param tokens - Array of Evolu cashuToken rows
 * @returns Array of cashu-ts Proof objects
 */
export function evoluToProofs(tokens: readonly EvoluTokenRow[]): CashuProof[] {
  return tokens.map((token) => ({
    amount: token.amount,
    secret: token.secret,
    C: token.c,
    id: token.keysetId,
  }));
}

/**
 * Group Evolu token rows by their mint ID.
 *
 * Useful for operations that need to process tokens per-mint,
 * such as balance calculation or batch verification.
 *
 * @param tokens - Array of Evolu cashuToken rows
 * @returns Object mapping mintId to arrays of token rows
 */
export function groupProofsByMint(
  tokens: readonly EvoluTokenRow[],
): Record<string, EvoluTokenRow[]> {
  const groups: Record<string, EvoluTokenRow[]> = Object.create(null) as Record<
    string,
    EvoluTokenRow[]
  >;

  for (const token of tokens) {
    const existing = groups[token.mintId];
    if (existing) {
      existing.push(token);
    } else {
      groups[token.mintId] = [token];
    }
  }

  return groups;
}
